import { endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { userRole } from "@/constant";
import { useSearchQuery } from "@/utils/useSearchQuery";
import { App, Button, Empty, Switch } from "antd";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { BiMessageSquareEdit, BiMinus, BiPlus } from "react-icons/bi";
import { FaPlus } from "react-icons/fa6";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";

import DashboardLayout from "../DashboardLayout";
import DashboardAddItemModal from "./DashboardAddItemModal";
import DashboardComponentModal from "./DashboardComponentModal";
import DashboardTabs from "./DashboardTabs";
import MetaDashboardModal from "./MetaDashboardModal";
import { flare } from "@flareapp/js";
import { ReactFlowProvider } from "@xyflow/react";
import LiveDashboardCanvas from "@/components/live-dashboard/canvas/LiveDashboardCanvas";
import { useSmoothTabZoom } from "@/hooks/useSmoothTabZoom";

const DashboardBody = ({
  mode,
  id,
  layouts,
  setLayouts,
  gridData = [],
  setGridData,
  selectedTiles = [],
  handleSelect = () => {},
  setTabId = () => {},
  isFullScreenActive = false,
}) => {
  const { userDetails } = useSelector((state) => state.user);
  const firstBusiness = userDetails?.business?.[0] || {};
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const cp = useSearchQuery("cp") || undefined;

  // state
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tabData, setTabData] = useState([]);
  const [isEditable, setIsEditable] = useState(false);
  // modal
  const [updateDashboardModal, setUpdateDashboardModal] = useState(false);
  const [componentModal, setComponentModal] = useState(false);
  const [addItemModal, setAddItemModal] = useState(false);
  const [editItem, setEditItem] = useState({});
  const [iframe, setIframe] = useState();
  const { tabZoom, targetTabZoom, handleTabZoomChange, handleResetTabZoom } = useSmoothTabZoom(1);

  const renderTabZoomControls = () => {
    if (!data?.hasTabs) return null;
    return (
      <div className="dashboard-tab-zoom-controls-theme">
        <span className="px-2 small fw-medium" style={{ color: "var(--color-black)", fontSize: "12px", whiteSpace: "nowrap" }}>
          Tabs Zoom:
        </span>
        <button
          type="button"
          className="zoom-btn"
          onClick={() => handleTabZoomChange(-0.1)}
          disabled={targetTabZoom <= 0.5}
          title="Zoom Out Tabs"
          aria-label="Zoom Out Tabs"
        >
          <BiMinus size={14} />
        </button>
        <span
          className="zoom-badge"
          onClick={handleResetTabZoom}
          title="Click to reset zoom"
        >
          {Math.round(targetTabZoom * 100)}%
        </span>
        <button
          type="button"
          className="zoom-btn"
          onClick={() => handleTabZoomChange(0.1)}
          disabled={targetTabZoom >= 1.6}
          title="Zoom In Tabs"
          aria-label="Zoom In Tabs"
        >
          <BiPlus size={14} />
        </button>
      </div>
    );
  };

  const { name, business, site, hasTabs } = data;

  const getDashboard = async () => {
    if (!isLoading) {
      setIsLoading(true);
      const res = await axios.get(endpoint.dashboard + "/" + id, { withCredentials: true });
      // console.log({ res });
      if (res.status === 200) {
        if (cp === "true") {
          let copyData = { ...res.data?.result, name: res.data?.result?.name + "(copy)", business: [], site: [] };
          if (!res.data?.result?.hasTabs && Array.isArray(res.data?.result?.items) && res.data?.result?.items?.length > 0) {
            copyData.tabs = [];
            copyData.items.forEach((item) => (item.locations = [{}]));
          } else if (res.data?.result?.hasTabs && Array.isArray(res.data?.result?.tabs) && res.data?.result?.tabs?.length > 0) {
            copyData.items = [];
            copyData.tabs.forEach((tab) => {
              if (Array.isArray(tab?.items) && tab?.items?.length > 0) {
                tab.items.forEach((item) => (item.locations = [{}]));
              }
            });
          }
          await setData(copyData);
          setUpdateDashboardModal(true);
        } else {
          const targetTabId = new URLSearchParams(window.location.search).get("tabId");
          if (targetTabId && res.data.result?.tabs?.length > 0) {
            const hasMatch = res.data.result.tabs.some((tab) => tab.tabId === targetTabId || tab._id === targetTabId);
            if (hasMatch) {
              res.data.result.tabs = res.data.result.tabs.map((tab) => ({
                ...tab,
                isSelected: tab.tabId === targetTabId || tab._id === targetTabId,
              }));
            }
          }
          setData(res.data.result);
        }
        const defaultTabs = {
          isSelected: true,
          tabId: uuid(),
          colorSchemeName: "orange",
          icon: "Fa500Px",
          items: gridData,
          layouts,
          name: "Tab 1",
        };
        if (res.data.result?.tabs?.length < 1) {
          setTabData([defaultTabs]);
        } else {
          setTabData(res.data.result?.tabs || []);
        }

        const findActiveTab = res.data.result?.tabs?.find((tab) => tab.isSelected) || res.data.result?.tabs?.[0] || {};

        const activeLayouts =
          (Object.keys(findActiveTab?.layouts || {}).length > 0
            ? findActiveTab?.layouts
            : res.data.result?.layouts || res.data.result?.tabs?.[0]?.layouts) || {};

        const activeGridData =
          (findActiveTab?.items?.length > 0 ? findActiveTab?.items : res.data.result?.items || res.data.result?.tabs?.[0]?.items) || [];

        setLayouts(activeLayouts);
        setGridData(activeGridData);
        setIframe(findActiveTab?.iframe || res.data.result?.iframe || res.data.result?.tabs?.[0]?.iframe);
        setIsLoading(false);
      }
    }
  };

  const handleUpdate = async () => {
    const body = { name, business, site };

    if (hasTabs) {
      body.tabs = tabData;
    } else {
      if (editItem.component === "iframe" || data?.iframe) {
        body.iframe = iframe;
      } else {
        body.layouts = layouts;
        body.items = gridData;
      }
    }

    // return;
    try {
      const response = await axios.put(endpoint.update_dashboard + "/" + data._id, body, { withCredentials: true });
      // console.log({ response });
      if (response.status === 204) {
        // Swal.fire("", "Dashboard Updated Successfully!", "success");
        notification.success({ key: "dashboard-save-notification", message: `Dashboard updated successfully.`, duration: 2, showProgress: true });
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
      notification.success({ message: `Error happened.`, duration: 2, showProgress: true });
    }
  };

  const handleCreate = async () => {
    const dashboardType = uuid();
    const body = { ...editItem, name, business, site, dashboardType, hasTabs };

    if (hasTabs) {
      body.tabs = tabData;
    } else {
      if (editItem.component === "iframe" && editItem.iframe) {
        body.iframe = editItem.iframe;
      } else {
        body.layouts = layouts;
        body.items = gridData;
      }
    }
    try {
      const response = await axios.post(endpoint.create_dashboard, body, { withCredentials: true });
      if (response.status === 204) {
        // Swal.fire("", "Dashboard Updated Successfully!", "success");
        notification.success({ message: `Dashboard created successfully.`, duration: 2, showProgress: true });
      }
    } catch (error) {
      flare.report(error);
      notification.success({ message: `Error happened.`, duration: 2, showProgress: true });
    }
  };

  const handleSubmit = async () => {
    if (cp === "true") {
      await handleCreate();
    } else {
      await handleUpdate();
    }
    navigate("/dashboard-list");
  };

  useEffect(() => {
    if (mode === "edit") {
      setIsEditable(true);
    }
  }, [mode]);

  useEffect(() => {
    if (isFullScreenActive) {
      setIsEditable(false);
    }
  }, [isFullScreenActive]);

  useEffect(() => {
    if (data?.hasTabs) {
      const updated = tabData.map((tab) => {
        if (tab.isSelected) {
          return { ...tab, layouts, items: gridData, iframe };
        }
        return tab;
      });
      setTabData(updated);
    }
  }, [gridData, layouts, data.hasTabs, iframe]);
  // }, [data.hasTabs, iframe]);

  //  may causes issue latter.
  useEffect(() => {
    if (data.hasTabs && Array.isArray(tabData) && tabData.length > 0) {
      const findItem = tabData.find((tab) => tab.isSelected) || tabData[0];
      if (findItem) {
        if (setTabId) setTabId(findItem._id || findItem.tabId);
      }
    }
  }, [tabData]);

  useEffect(() => {
    setData({});
    getDashboard();
  }, [id]);

  useEffect(() => {
    document.title = `${appConfig.name} - Dashboard`;
  }, []);

  const toggleHandler = (e) => {
    setIsEditable(e);
    if (!e) {
      Swal.fire({
        title: "Save Changes?",
        text: "By clicking confirm, the changes will be saved. Click cancel to abandon any edits.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          handleUpdate();
        } else {
          getDashboard();
        }
      });
    }
  };

  const logo = firstBusiness?.logo || "/img/logo.png";
  return (
    <>
      <div>
        {/* <h5>Dashboard List</h5> */}
        <div className="d-sm-flex align-items-center justify-content-between">
          {mode === "home" && isFullScreenActive && (
            <div className="p-3 ps-2">
              <img src={logo} alt="Logo" style={{ height: "50px" }} />
            </div>
          )}
          {mode !== "home" && (
            <div>
              <label className="az-content-label">Manage Dashboard</label>
              <span className="d-block py-2">
                {mode === "view" ? "View" : "Edit"} Dashboard: {data?.name}
              </span>
            </div>
          )}

          {/* edit options */}
          {![userRole.public, userRole.user].includes(userDetails?.role) && mode !== "view" && (
            <div className="d-flex flex-column-reverse flex-sm-row gap-2 align-items-sm-center align-items-end ms-auto me-4">
              {isEditable && (
                <>
                  <Button
                    className="fw-medium bg_light_gray"
                    type="primary"
                    icon={<BiMessageSquareEdit />}
                    onClick={() => setUpdateDashboardModal((pre) => !pre)}
                  >
                    Edit Dashboard
                  </Button>
                  <Button
                    type="primary"
                    className="fw-medium bg_light_gray"
                    onClick={() => setComponentModal((pre) => !pre)}
                    icon={<FaPlus />}
                    disabled={data.hasTabs && !tabData?.find((tab) => tab.isSelected)}
                  >
                    Add Items
                  </Button>
                </>
              )}
              {mode === "home" && !isFullScreenActive && (
                <div className="d-flex align-items-center gap-2">
                  {renderTabZoomControls()}
                  <Switch checked={isEditable} onChange={toggleHandler} />
                </div>
              )}
            </div>
          )}
        </div>
        {mode !== "home" && <hr className="mt-0 mb-4" />}

        {/* tabs */}
        <div className="mb-3">
          {data.hasTabs && (
            <>
              <DashboardTabs
                {...{
                  mode: [userRole.public, userRole.user].includes(userDetails?.role) || !isEditable ? "view" : "create",
                  tabData,
                  setTabData,
                  setLayouts,
                  setGridData,
                  dashboard: data,
                  setIframe,
                  tabZoom,
                }}
              />
              {mode !== "home" && <hr className="mt-4" />}
            </>
          )}
        </div>

        {/* body */}

        {data?.type === "live" ? (
          <div className="pb-5" style={{ height: "70vh", minHeight: "500px" }}>
            <ReactFlowProvider key={`${id || data?._id}-${tabData?.find((t) => t?.isSelected)?.tabId || tabData?.find((t) => t?.isSelected)?._id || "default"}`}>
              <LiveDashboardCanvas
                key={`${id || data?._id}-${tabData?.find((t) => t?.isSelected)?.tabId || tabData?.find((t) => t?.isSelected)?._id || "default"}`}
                dashboardId={id || data?._id}
                tabId={tabData?.find((t) => t?.isSelected)?.tabId || tabData?.find((t) => t?.isSelected)?._id}
                readOnly={!isEditable}
              />
            </ReactFlowProvider>
          </div>
        ) : (
          <>
            {iframe ? (
              <>
                {isEditable && (
                  <div className="mb-3 d-flex align-items-center gap-4 bg-light p-2">
                    {iframe}
                    <div style={{ cursor: "pointer" }} onClick={() => setIframe(null)}>
                      <IoMdCloseCircleOutline />
                    </div>
                  </div>
                )}
                <iframe title="Dashboard" src={iframe} style={{ width: "100%", height: "60vh" }}>
                  Iframe does not supported
                </iframe>
              </>
            ) : gridData?.length > 0 ? (
              <div className="pb-5">
                <div className="bg-layout" key={tabData?.find((tab) => tab?.isSelected)?.tabId}>
                  <DashboardLayout
                    {...{
                      gridData,
                      setGridData,
                      layouts,
                      setLayouts,
                      mode: isEditable ? "create" : "view",
                      setComponentModal,
                      setEditItem,
                      selectedTiles,
                      handleSelect,
                      mainDashboard: mode === "home",
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="m-auto py-5">
                {/* <Empty description="No Data. Add Some " /> */}
                <Empty />
              </div>
            )}
          </>
        )}

        {/* buttons */}
        {!["home", "view"].includes(mode) && (
          <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
            <Button type="primary" onClick={handleSubmit} disabled={data?.business?.length <= 0 || data?.site?.length <= 0}>
              {cp === "true" ? "Create" : mode === "edit" ? "Update" : "Create"}
            </Button>
            <Button type="primary" className="bg-secondary" onClick={() => navigate("/dashboard-list")} htmlType="button">
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="relative" style={{ zIndex: 9999999999 }}>
        {updateDashboardModal && (
          <MetaDashboardModal
            {...{
              show: updateDashboardModal,
              setShow: setUpdateDashboardModal,
              mode: cp === "true" ? "copy" : "edit",
              data,
              setData,
              setGridData,
              setLayouts,
              tabData,
              setTabData,
              getDashboard,
            }}
          />
        )}
        {componentModal && (
          <DashboardComponentModal
            {...{
              show: componentModal,
              setShow: setComponentModal,
              mode: "create",
              editItem,
              setEditItem,
              setAddItemModal,
              setGridData,
              gridData,
              data,
              iframe,
            }}
          />
        )}
        {addItemModal && (
          <DashboardAddItemModal
            {...{
              show: addItemModal,
              setShow: setAddItemModal,
              setComponentModal,
              mode: gridData.find((item) => item._id === editItem._id) ? "edit" : "create",
              editItem,
              setEditItem,
              data,
              gridData,
              setGridData,
              setData,
              iframe,
              setIframe,
            }}
          />
        )}
      </div>
    </>
  );
};

export default DashboardBody;

DashboardBody.propTypes = {
  mode: PropTypes.string,
  id: PropTypes.string,
  layouts: PropTypes.array,
  setLayouts: PropTypes.func.isRequired,
  gridData: PropTypes.array,
  setGridData: PropTypes.func.isRequired,
  selectedTiles: PropTypes.array,
  handleSelect: PropTypes.func.isRequired,
  setTabId: PropTypes.func.isRequired,
  isFullScreenActive: PropTypes.bool,
};
